import type { ComponentType, SVGProps } from "react";

import { DashboardTaskAddButton } from "./DashboardTaskAddButton";

import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import {
  AccountGroup4Icon,
  Calendar2Icon,
  CalendarCheck2Icon,
  Clock2Icon,
  ListBullets2Icon,
} from "@/components/streamline";

import { cn } from "@/lib/utils";
import { DashboardScheduleTaskCategory } from "@/types";

const categoryChipConfig: Record<
  DashboardScheduleTaskCategory,
  {
    Icon: ComponentType<SVGProps<SVGSVGElement>>;
    label: string;
    chipClassName: string;
    iconClassName: string;
  }
> = {
  // Untyped user tasks; not surfaced in the counts band, kept for exhaustiveness.
  [DashboardScheduleTaskCategory.GENERAL]: {
    Icon: ListBullets2Icon,
    label: "General",
    chipClassName:
      "border-lever-border-light bg-lever-surface-default-on-elevation text-lever-text-body",
    iconClassName: "text-lever-text-footnote",
  },
  [DashboardScheduleTaskCategory.CHECK_INS]: {
    Icon: CalendarCheck2Icon,
    label: "Check-ins",
    chipClassName:
      "border-lever-border-warning bg-lever-surface-warning/40 text-lever-text-warning",
    iconClassName: "text-lever-text-warning",
  },
  [DashboardScheduleTaskCategory.STARTS]: {
    Icon: AccountGroup4Icon,
    label: "Starts",
    chipClassName:
      "border-lever-border-success bg-lever-surface-success/40 text-lever-text-success",
    iconClassName: "text-lever-text-success",
  },
  [DashboardScheduleTaskCategory.INTERVIEWS]: {
    Icon: Calendar2Icon,
    label: "Interviews",
    chipClassName:
      "border-lever-border-information bg-lever-surface-information/40 text-lever-text-information",
    iconClassName: "text-lever-text-information",
  },
  [DashboardScheduleTaskCategory.MISSING_START]: {
    Icon: Clock2Icon,
    label: "Missing start",
    chipClassName:
      "border-lever-border-error bg-lever-surface-error/40 text-lever-text-error",
    iconClassName: "text-lever-text-error",
  },
};

// Definitive section header for the expanded task list — icon + label on a
// slightly raised background.
export const DashboardScheduleWeekTaskCategoryHeader = ({
  category,
  showAddButton = false,
  onAddClick,
}: {
  category: DashboardScheduleTaskCategory;
  showAddButton?: boolean;
  onAddClick?: () => void;
}) => {
  const config = categoryChipConfig[category];
  const Icon = config.Icon;

  const render = () => (
    <Row className="items-center justify-between border-b border-lever-border-light bg-lever-surface-default-on-elevation/80 px-4 py-2">
      <Row className="items-center gap-1.5">
        <Icon className={cn("h-3.5 w-3.5 shrink-0", config.iconClassName)} />
        <Text className="font-lever-suisse text-xs font-semibold text-lever-text-footnote">
          {config.label}
        </Text>
      </Row>
      {showAddButton && onAddClick ? (
        <DashboardTaskAddButton onClick={onAddClick} />
      ) : null}
    </Row>
  );

  return render();
};

export const DashboardScheduleWeekTaskCategoryChip = ({
  category,
  count,
}: {
  category: DashboardScheduleTaskCategory;
  count: number;
}) => {
  const config = categoryChipConfig[category];
  const Icon = config.Icon;

  const render = () => (
    <Row
      className={cn(
        "inline-flex min-h-6 max-w-full shrink-0 items-center gap-1.5 rounded-none px-2 py-1",
        config.chipClassName,
      )}
    >
      <Icon className={cn("h-3 w-3 shrink-0", config.iconClassName)} />
      <Text className="font-lever-accent text-[11px] font-normal leading-none text-inherit">
        {count.toString()}
      </Text>
      <Text className="truncate font-lever-suisse text-[11px] font-normal leading-none text-lever-text-body">
        {config.label}
      </Text>
    </Row>
  );

  return render();
};
